// src/encounter/pre-encounter-rail-view.tsx
// Presentational — no data fetch. All state flows in via props.
//
// The pre-encounter landing (encounter status Initial → before Start): a TWO-column layout —
// Patient-Summary rail LEFT (header inside) · workflow MIDDLE. The right Note/Orders drawer is
// deliberately NOT mounted here (PROD-1588 wave S2, Samer's screen-2 reject + the contract's
// pre-visit ruling): before Start there is no note/orders content, so the drawer zone is hidden
// on THIS step only — the in-encounter steps keep the full 3-column shell with the right drawer
// (encounter-shell-view.tsx, Contract §4 right-drawer zone / §10 layout gate).
// Source: pre_encounter_widget.dart + encounter_screen.dart (E2 frame 1736:156432).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  EncounterNoteDock,
  PreEncounterScreen,
  type PatientHeaderData,
  type PatientHeaderDemographics,
  type IpsSection,
  type VitalsRowVitals,
  type VitalsRowPatientType,
} from '@scripsteam/scrips-signal-ds';
import { startActionLabel, type StartAction, type VisitModality } from './lib/reason-for-visit';
import type { PatientVital } from '@/types/vitals-api';
import { LeftPanelToggle, useLeftPanelFold } from './left-panel-fold';
import { chooseContext, choosePanel, isZoningOn, useEncounterZoning } from './encounter-zoning';
import { PreEncounterTabStrip, type PreEncounterVisitContext } from './pre-encounter-context-strip';
import { VideoJoinNotice } from './video-join-notice';
import { EncounterStepBar } from './encounter-step-bar';
import { PatientSummaryRail, RailStatusControl, type RailSocialEntry } from './patient-summary-rail';
import type { PatientFlag } from '@/types/patient-summary-api';
import { CarePathwayRailContainer, type CarePathwayTab } from './care-pathway-rail';
import type { NursePrevisitHost } from './nurse-previsit/nurse-previsit-template';
import { IntelligenceFab } from './intelligence-rail';
import { IntelligenceCloseContext } from './intelligence/rail-close';

import type { CarePathwayActivity } from './lib/care-pathway';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import type { ContactVM } from './lib/to-patient-contacts';
import type { PatientOccupation, PatientEducation } from '@/types/patient-detail-api';
import { MessageColleagueAction } from '@/staff-chat/components/message-colleague-action';
import { isStaffChatPatientOn } from '@/staff-chat/lib/staff-chat-switch';
import type { SubmittedFormView } from './lib/to-submitted-questionnaire';
import { EncounterWorkflow, type ActiveMedicationItem } from './encounter-workflow';
import type { NurseNotesView } from './nurse-notes-section';
import type { VaccineAdministrationRequestView } from './lib/to-vaccination-requests';
import type { PreviousNoteView } from './lib/to-previous-notes';
import type { ShellRole } from './lib/encounter-blocks';
import type { MedicationAdminRequestView } from './lib/to-medication-admin-requests';
import { openMedicationReconciliation, previsitDrawsReconciliation } from './lib/open-medication-reconciliation';

/** Before Start the progression bar holds ONE cell, Pre-visit (Antonin, 19 Oct: "pre-visit is the first step of the
 *  encounter"), current, read only, with Start at its right. The SOAP cells appear when the encounter starts and this cell
 *  goes (the started view draws the SoapRibbon). */
const PRE_VISIT_STEP = { id: 'pre-visit', label: 'Pre-visit' };
const PRE_VISIT_STEPS = [PRE_VISIT_STEP];

/** The primary's label on the pre-Start view: "Start encounter" for a visit not yet started; with `?x=template-review` (dev default
 *  ON) for one in progress too (PROD-3392, Antonin 3 Nov: "start encounter, not continue"; the catalog's pre-visit strip ends in
 *  Start encounter). Switch OFF: an in-progress visit reads Continue (28 Oct, Ahmed). Edit and Retry keep their own words. Label
 *  only: the StartAction, the handler and the gate are unchanged. */
export function preStartLabel(action: StartAction, review: boolean = isEncounterFlagOn('templateReview')): string {
  if (action === 'start' || (review && action === 'continue')) return 'Start encounter';
  return startActionLabel(action);
}

export interface ReasonForVisit {
  /** Follow-up vs first-time visit (appointment_type.code). */
  appointmentType?: 'follow-up' | 'first';
  /** reasonCode.display. */
  reason?: string;
  /** Visit modality (Flutter appointment.location.name → locationIcon): a 24px blue
   *  office/home/virtual glyph left of the reason text. Undefined → glyph hidden. */
  locationType?: VisitModality;
  /** Free-text appointment comment (rendered in the alert hue when present). */
  comment?: string;
}

export interface PreEncounterRailViewProps {
  /**
   * Chart tab body (PROD-1986) — rendered IN PLACE of the pre-encounter workflow when
   * `visitContext.activeTab === 'chart'`, with the top tab strip left mounted above it (Flutter
   * `chartViewMainPanel`). OPTIONAL: absent → the Chart tab is never shown as active and this
   * surface behaves exactly as before (Storybook / no-chart paths).
   */
  chart?: ReactNode;
  /**
   * Documents tab body: this visit's documents (the Docs step's own list). Rendered IN PLACE of the workflow when
   * `visitContext.activeTab === 'documents'`, the left panel kept and no care-pathway column. Absent → the tab is not offered.
   */
  documents?: ReactNode;
  /** Timeline tab body: the visit's care-pathway history. Rendered in place of the workflow when `visitContext.activeTab === 'timeline'`,
   *  the left panel kept and no care-pathway column. Absent → the tab is not offered. */
  timeline?: ReactNode;
  /** The Timeline slot's header name ("Care plan" under `?x=care-plan-tab`). */
  timelineLabel?: string;
  /** Terrain tab body (`?x=terrain`): the patient's body systems, in place of the workflow when `visitContext.activeTab === 'terrain'`,
   *  the left panel kept and no care-pathway column. Absent → the tab is not offered. */
  terrain?: ReactNode;
  /**
   * `?x=previsit-intel`: the encounter's own Scrips Intelligence rail (phase "Pre-visit"), built by the page. With the switch
   * on it is the right column on the Encounter tab, in place of Care pathway / Note. Absent or switch off: no change.
   */
  intelligence?: ReactNode;
  /** PROD-3388: the orb the closed rail's button holds (IntelligenceStrip), built by the page from its own orb state, the same
   *  state as the rail header's. Absent: the button holds no orb. */
  intelligenceStrip?: ReactNode;
  /**
   * The patient-history BAND that sits above the chart on the Chart tab (PROD-1986) — the same
   * `PatientSummaryRail layout="banner"` node the in-encounter shell mounts, passed by the page so
   * both surfaces show one summary. Absent → the chart fills the pane with no band.
   */
  chartBand?: ReactNode;
  patient: PatientHeaderData;
  demographics?: PatientHeaderDemographics;
  /** Display/workflow hint only — NOT a permission gate. */
  role: 'doctor' | 'nurse';

  // ── Reason for Visit + Start ──────────────────────────────────────────────
  reason: ReasonForVisit;
  /** Primary action, derived from encounter status by the page (startActionFor). */
  startAction?: StartAction;
  /** Display-only gating message; when set, the Start button is disabled. */
  startDisabledReason?: string;
  onStart?: () => void;
  /** Reschedule action (PROD-1246). Optional + page-safe: when omitted, no Reschedule button
   *  renders — so reload/deep-link without the appointment slot (the dialog target) simply
   *  hides it. Flutter's own affordance is a dead stub; this is functional per Samer's
   *  "Start/Reschedule" scope, reusing the shipped reschedule flow (PROD-1193). */
  onReschedule?: () => void;

  // ── Chief complaint ───────────────────────────────────────────────────────
  chiefComplaint: string;
  onChiefComplaintChange: (next: string) => void;
  ccSuggestions?: string[];
  ccSelected?: string[];
  onCcSelectionChange?: (next: string[]) => void;
  /** Commit free-typed text as a complaint (DS "+ Create new" — Flutter tick-button twin). */
  onCcCreate?: (value: string) => void;
  /** Chief-complaint save in flight — surfaces the honest "Saving…" note under the field. */
  ccSaving?: boolean;
  /**
   * LIVE since 2026-07-23 (Samer live-review verdict, PROD-1588): persistence is wired
   * (AISearch RAG suggestions + CreateChiefComplaint replace-all + GetPatientComplaint
   * re-read — see use-chief-complaint.ts). Deferred remains ONLY for the window where the
   * encounter guid hasn't resolved yet (the POST requires it) — the editor renders `inert`
   * with an honest caption so a clinician can't type text that silently vanishes.
   */
  chiefComplaintDeferred?: boolean;
  /** Lock the chief-complaint field — Booked / Pending / Proposed (Flutter's own rule). */
  chiefComplaintLocked?: boolean;

  // ── Vitals ────────────────────────────────────────────────────────────────
  vitals: VitalsRowVitals;
  patientType?: VitalsRowPatientType;
  onVitalsReviewed?: () => void;
  /** False → "Vitals Reviewed" renders disabled (nothing recorded to review). Pass-through
   *  to EncounterWorkflow (vitals lane, PROD-1536). */
  canReviewVitals?: boolean;
  onVitalsHistorical?: () => void;
  onVitalsEdit?: () => void;
  /** Open the visual-acuity capture dialog (PROD-1537). */
  onVisualAcuity?: () => void;
  /** Vitals reviewed-state (PROD-1536) — shows a green check beside the Vitals title. */
  vitalsReviewed?: boolean;
  /** Inline vitals edit — the grid edits in place (see EncounterWorkflow.onVitalsSubmit). */
  onVitalsSubmit?: (form: Record<string, string>) => void;
  /** Catalogue gating (LMP female>8, head circ age<=2) — same rule as the appointment tab. */
  patientGender?: 'male' | 'female' | null;
  patientAge?: number | null;
  vitalsExisting?: PatientVital[] | null;
  vitalsSaving?: boolean;
  /** PROD-2947 · B12: a refused vitals save, said in place (see EncounterWorkflow). */
  vitalsNotice?: string;
  /** "Patient device data", its own section directly below the vitals (see EncounterWorkflow `deviceData`). */
  deviceData?: ReactNode;
  /** `ambientWindow` (B1): the ambient card above the pre-visit cards (see EncounterWorkflow `ambientCard`). */
  ambientCard?: ReactNode;
  /** The nurse's pre-visit as Antonin designed it, the medication reconciliation included (see EncounterWorkflow `nursePrevisit`). */
  nursePrevisit?: NursePrevisitHost;
  /** [PROD-2643] Diagnostics and imaging (see EncounterWorkflow `previsitDiagnostics`). */
  previsitDiagnostics?: ReactNode;
  /** Visual Acuity capture form, rendered inline under the vitals grid while editing. */
  visualAcuity?: ReactNode;
  /** Cancel on the acuity panel — revert the draft (Flutter discards without committing). */
  onAcuityCancel?: () => void;
  /** Virtual-visit banner (Flutter videoStartWidget). Connect STARTS the consult; absent
   *  handlers mean telemedicine isn't configured, so the advisory renders without an action. */
  videoInCall?: boolean;
  onVideoConnect?: () => void;
  onVideoLeave?: () => void;
  /** Why Connect is not offered outside the video join window ("Video opens at 2:50 PM"). */
  videoJoinReason?: string;
  /** Acuity cell value (OS/OD summary). */
  visualAcuitySummary?: string | null;
  /** Growth chart node (PROD-1538), age/sex-gated by the page; forwarded to the middle column
   *  where it renders right after Vitals. Absent → nothing renders (adults). */
  growthChart?: ReactNode;

  /** Submitted-questionnaire view model (PROD-1251); null/absent → section hidden. */
  submittedForm?: SubmittedFormView | null;
  /** Submitted-QR fetch failed → show a note instead of silently hiding. */
  submittedFormError?: boolean;

  // ── IPS rail ──────────────────────────────────────────────────────────────
  ipsSections: IpsSection[];
  /** Raw patient flags (PROD-1989) — forwarded to PatientSummaryRail, which needs the numeric
   *  `flagResponse.code` to build the whole-list-replace body. Absent → flags are display-only. */
  flagRecords?: PatientFlag[] | null;

  /** Left-rail Occupation/Education card rows (PROD-1259 #5), derived from the patient detail by
   *  the page (PROD-1270). Each is the list of labeled rows for that card. */
  occupation?: RailSocialEntry[];
  education?: RailSocialEntry[];
  /** Raw Occupation/Education entities (PROD-1272) — forwarded to the rail for the edit round-trip
   *  + dialog pre-fill. */
  occupationEntity?: PatientOccupation | null;
  educationEntity?: PatientEducation | null;
  /** Inline active-medications list, middle column (PROD-1259 #7), derived from the summary. */
  activeMedications?: ActiveMedicationItem[];
  /** Vaccination administration requests (PROD-1250), middle column — derived by the page from the
   *  raw PatientAssessment (toVaccinationRequests). Section hidden when empty (Flutter parity). */
  vaccinationRequests?: VaccineAdministrationRequestView[];
  /** Open the lot-aware administration editor for a request (PROD-1227 seam). Omitted →
   *  display-only rows (no dead action button). */
  onAdministerVaccination?: (request: VaccineAdministrationRequestView) => void;
  /** Previous completed encounter notes (follow-up, PROD-1523) — hidden when empty. */
  previousNotes?: PreviousNoteView[];
  onViewPreviousNote?: (note: PreviousNoteView) => void;
  /** [PROD-1395] The clinician's role, for the allied-health "Referral" card (flag `referrals`). */
  encounterRole?: ShellRole;
  /** Medication administration requests (A/P meds freq=Now, PROD-1524) — hidden when empty. */
  medicationAdminRequests?: MedicationAdminRequestView[];
  onGiveMedication?: (request: MedicationAdminRequestView) => void;
  /** Nurse pre-visit handoff (E2 "Notes from Nurse" block) — hidden when absent; no feed yet. */
  nurseNotes?: NurseNotesView | null;
  /** `?x=nurse-note-card`: the card with its own data (see EncounterWorkflow `nurseNotesNode`). */
  nurseNotesNode?: ReactNode;
  /** Care-pathway activities for the design's RIGHT rail — an OVERRIDE for hosts that already
   *  hold the feed (stories/tests). Normally omitted: the rail now reads its own history off
   *  `appointmentId` (PROD-738). The rail mounts whenever the screen renders (it is a fixed
   *  column of PreEncounter.dc.html); the LIST inside distinguishes loading / failed / no feed
   *  / empty pathway — see care-pathway-rail.tsx. */
  carePathway?: CarePathwayActivity[];
  /**
   * Note panel for the right rail's Note tab (PROD-1988). Flutter's `sidePanel()`
   * (`encounter_screen.dart:1657`) mounts the generated note beside BOTH the pre-encounter
   * columns and the started encounter, so this screen having a Note tab is parity; until now the
   * tab rendered inert because nothing was passed to it. Absent → it stays inert.
   */
  note?: ReactNode;

  /**
   * When set, the IPS Flags section becomes interactive (add + delete) and persists to
   * /api/Patients/CreateFlag (whole-list replace). Optional by design: absent → the rail is
   * display-only, so the encounter-shell-page call-site need not change (no PROD-1144 conflict).
   */
  patientId?: string;
  /** Provider id stamped onto a newly-added flag (CreateFlag createdBy) + the medication-search
   *  ProviderId + the allergy `recorder` / condition `asserter` on CREATE. Optional. */
  currentUserId?: string;
  /** Current user's display name — stamped on the condition CREATE performer sentinel. Optional. */
  currentUserName?: string | null;
  /** Encounter id — enables medication add on the rail (CreateMedication.encounter, PROD-1257). */
  encounterId?: string;
  /** Practitioner id (CreateMedication administration.performer); falls back to currentUserId. */
  performerId?: string;
  /** PROD-2669: offer "Stop" on a medication row. Physicians only (`useRole().mayStopOrSuspend`,
   *  the Scrips.Common allowlist {3, 8, 18}); a nurse reconciles with Edit but does not stop.
   *  Defaults to FALSE so a host that forgets to pass it offers no stop. */
  canStopMedication?: boolean;

  /** Patient-card extras (PROD-1329) — forwarded to PatientSummaryRail → the DS card. Same shapes
   *  as the in-encounter rail, so the card matches on BOTH surfaces (was enc-only). */
  appointmentStatus?: string;
  /** Appointment id — forwarded to PatientSummaryRail so the pre-encounter also shows the
   *  interactive status control (change status before/at Start), not just the read-only badge. */
  appointmentId?: string;
  importance?: { code?: string | null; display?: string | null; definition?: string | null } | null;
  contacts?: ContactVM[];

  /** Skeleton hint while data hooks resolve. */
  loading?: boolean;
  /** PROD-2804 / R6: the patient-summary read FAILED — forwarded to PatientSummaryRail, which
   *  shows "couldn't be loaded" + Try again instead of empty sections. */
  loadError?: boolean;
  /** Retry for `loadError`. */
  onRetry?: () => void;
  /** PROD-2804 / R6: sections whose summary field was not delivered — forwarded to the rail. */
  unavailableSections?: readonly string[];

  /**
   * Top context chrome (PROD-1588 overlay-gate iteration 2 — the E2 pre-encounter frame's
   * tab strip + room/time/RFV/status block): when present, the view renders the 43px
   * Encounter|Chart tab strip above BOTH columns and the DS EncounterContextStrip + status
   * pill at the top of the LEFT column. Every cell is data-gated (anti-fiction) — see
   * pre-encounter-context-strip.tsx. Absent → the two-column layout renders exactly as before.
   */
  visitContext?: PreEncounterVisitContext;
}

export function PreEncounterRailView(props: PreEncounterRailViewProps) {
  const {
    chart,
    documents,
    timeline,
    timelineLabel,
    terrain,
    intelligence,
    intelligenceStrip,
    chartBand,
    patient,
    demographics,
    ipsSections,
    flagRecords,
    occupation,
    education,
    occupationEntity,
    educationEntity,
    patientId,
    currentUserId,
    currentUserName,
    encounterId,
    performerId,
    canStopMedication,
    appointmentStatus,
    appointmentId,
    importance,
    contacts,
    loading,
    loadError,
    onRetry,
    unavailableSections,
    // `role` is part of the contract (role-aware workflow) but v1 renders the same
    // pre-encounter layout for doctor + nurse; consumed when role-specific framing lands.
    reason,
    startAction,
    startDisabledReason,
    onStart,
    onReschedule,
    chiefComplaint,
    onChiefComplaintChange,
    ccSuggestions,
    ccSelected,
    onCcSelectionChange,
    onCcCreate,
    ccSaving,
    chiefComplaintDeferred,
    chiefComplaintLocked,
    vitals,
    patientType,
    onVitalsReviewed,
    canReviewVitals,
    onVitalsHistorical,
    onVitalsEdit,
    onVisualAcuity,
    vitalsReviewed,
    onVitalsSubmit,
    vitalsExisting,
    vitalsSaving,
    vitalsNotice,
    deviceData,
    ambientCard,
    nursePrevisit,
    previsitDiagnostics,
    patientGender,
    patientAge,
    visualAcuity,
    onAcuityCancel,
    videoInCall,
    onVideoConnect,
    onVideoLeave,
    videoJoinReason,
    visualAcuitySummary,
    growthChart,
    submittedForm,
    submittedFormError,
    activeMedications,
    vaccinationRequests,
    onAdministerVaccination,
    previousNotes,
    onViewPreviousNote,
    encounterRole,
    medicationAdminRequests,
    onGiveMedication,
    nurseNotes,
    nurseNotesNode,
    carePathway,
    note,
    visitContext,
  } = props;
  // Status pill placement: the PERSON CARD owns it (patient-summary-rail.tsx headerBlock),
  // matching PreEncounter.dc.html, which puts the appointment status chip beside the patient
  // name. The context block that used to host it is gone (see the left-column note below), so
  // there is no longer a second candidate location and no suppression to do.

  // The left panel's fold (prototype RecordPatientRail): local UI state, the toggle on the header's hairline.
  const leftPanel = useLeftPanelFold();
  // `?x=intel-always-open` (Antonin's zoning, encounter-zoning.ts): Scrips Intelligence takes the right column on every tab,
  // open on arrival; under the 480 floor the nav rail folds, then Intelligence becomes an overlay; the left panel and Intelligence are independent and the clinician's
  // choices. Only where the page passed the rail (frame v2 with the engine and a patient). OFF: every value below as before.
  // It builds on `?x=previsit-intel` (the rail as the pre-visit's right column): `-previsit-intel` keeps today's Care pathway.
  const [zoningFlag] = useState(() => isZoningOn() && isEncounterFlagOn('previsitIntel'));
  const zoning = useEncounterZoning(zoningFlag && intelligence != null);
  const leftFolded = zoning ? !zoning.contextShown : leftPanel.folded;
  const toggleLeft = zoning ? () => chooseContext(!zoning.contextShown) : leftPanel.toggle;

  // Rail v3 (`?x=rail-v3`): ONE page header, as on the started encounter (Andrew, 3 Nov: "Duplication in headers in the pre
  // encounter screen. It should be like the one in the encounter."). The header carries the identity (the encounter's facts
  // line) and the status control; the rail's person card keeps the contacts only. Behind `?x=previsit-header` (dev default ON;
  // `?x=-previsit-header` shows today's pre-visit). Read once per mount, as the rail reads it.
  const [oneHeader] = useState(() => isEncounterFlagOn('railV3') && isEncounterFlagOn('previsitHeader'));
  // The page draws the #916 reconciliation card only on the nurse's Signal pre-visit (encounter-workflow.tsx reads the same flag once
  // per mount and the same rule), so Reconcile is offered only there (left-rail target §7.6).
  const [signalPrevisit] = useState(() => isEncounterFlagOn('signalPrevisit'));
  const drawsReconciliation = previsitDrawsReconciliation({ signalPrevisit, encounterRole, hasNurseTemplate: !!nursePrevisit });

  const leftRail = (
    <PatientSummaryRail
        identityInHeader={oneHeader}
        patient={patient}
        demographics={demographics}
        ipsSections={ipsSections}
        flagRecords={flagRecords}
        occupation={occupation}
        education={education}
        occupationEntity={occupationEntity}
        educationEntity={educationEntity}
        patientId={patientId}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        encounterId={encounterId}
        performerId={performerId}
        canStopMedication={canStopMedication}
        appointmentStatus={appointmentStatus}
        appointmentId={appointmentId}
        importance={importance}
        contacts={contacts}
        loading={loading}
        loadError={loadError}
        onRetry={onRetry}
        unavailableSections={unavailableSections}
        // Left-rail target §3 / §7.6: "Reconcile medications" opens the reconciliation card (#916) on this page, only where the page
        // draws that card (the same rule the workflow mounts it by); everyone else gets no Reconcile action.
        {...(drawsReconciliation ? { onReconcileMedications: () => void openMedicationReconciliation() } : {})}
      />
  );

  // Which rail panel is open. Local because it is pure view state — Flutter's own side panel
  // tab controller is likewise screen-local (encounter_screen.dart:1657 builds it per render).
  const [railTab, setRailTab] = useState<CarePathwayTab>('timeline');
  // `?x=previsit-hide-pathway` (Antonin, 2 Nov): no right column (Care pathway, Note) before Start. Read once per mount.
  const [hidePathway] = useState(() => isEncounterFlagOn('previsitHidePathway'));
  // `?x=previsit-intel` (Antonin, 2 Nov): the Intelligence rail takes the right column, whatever previsit-hide-pathway says.
  const [showIntel] = useState(() => isEncounterFlagOn('previsitIntel'));
  // `?x=intel-close` (PROD-3388, ON on the dev hosts): the rail gets a close and the orb button to reopen it. Read once per mount.
  const [intelCloseOn] = useState(() => isEncounterFlagOn('intelClose'));
  const [intelOpenLocal, setIntelOpen] = useState(true);
  const intelOpen = zoning ? zoning.panelShown : intelOpenLocal;
  const intelFabRef = useRef<HTMLButtonElement>(null);
  const closeIntel = () => (zoning ? choosePanel(false) : setIntelOpen(false));
  // The overlay (the zoning under the 480 floor) behaves as the started visit's does: opened from the orb button, focus moves
  // into it; Escape closes it (unless a layer inside handled Escape), and focus returns to the orb button (the effect below).
  const overlayRef = useRef<HTMLDivElement>(null);
  const focusOverlay = useRef(false);
  const intelOverlayOn = !!zoning?.panelOverlay;
  const openIntel = () => {
    if (zoning) {
      focusOverlay.current = true;
      choosePanel(true);
    } else setIntelOpen(true);
  };
  // The host starts where the screen's pane starts, under the page header AND the video notice under it (Connect / Leave
  // stay clickable; Codex, fifth pass). Measured every render (a notice appears with props); a layout custom property on
  // the host itself, read by its own top class (the ledgered runtime property).
  useLayoutEffect(() => {
    const host = overlayRef.current;
    const row = host?.parentElement;
    const pane = row?.querySelector<HTMLElement>('[data-slot="pre-encounter-body"], [data-slot="pre-encounter-chart-pane"]');
    if (!host || !row || !pane) return;
    const top = Math.max(0, Math.round(pane.getBoundingClientRect().top - row.getBoundingClientRect().top));
    host.style.setProperty('--intel-host-top', `${top}px`);
  });
  // Consumed by the open it was armed for: an overlay open takes focus in, a docked open clears it (Codex, fifth pass).
  const intelShownNow = intelOpen;
  useEffect(() => {
    if (!intelShownNow || !focusOverlay.current) return;
    focusOverlay.current = false;
    if (!intelOverlayOn) return;
    overlayRef.current?.querySelector<HTMLElement>('button:not([disabled]), [href], input, textarea, select, [tabindex]:not([tabindex="-1"])')?.focus();
  }, [intelShownNow, intelOverlayOn]);
  useEffect(() => {
    if (!intelOverlayOn) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      choosePanel(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [intelOverlayOn]);
  // The X goes with the rail: focus moves to the button that reopens it.
  const intelWasOpen = useRef(intelOpen);
  useEffect(() => {
    if (!intelOpen && intelWasOpen.current) intelFabRef.current?.focus();
    intelWasOpen.current = intelOpen;
  }, [intelOpen]);
  const railProps = {
    // The rail owns the care-pathway read (PROD-738); `carePathway` stays as the host override.
    appointmentId,
    activities: carePathway,
    note,
    activeTab: railTab,
    // Only offer navigation when there is a second panel to navigate to.
    onTabChange: note ? setRailTab : undefined,
  };

  // PROD-1986: the Chart tab body replaces the workflow pane; gated on the slot so hosts that
  // pass no chart (Storybook / no-patient paths) are untouched.
  const showChart = visitContext?.activeTab === 'chart' && !!chart;
  // The Documents tab: this visit's documents replace the workflow, the left panel stays, no care-pathway column.
  const showDocuments = visitContext?.activeTab === 'documents' && !!documents;
  const showTimeline = visitContext?.activeTab === 'timeline' && !!timeline;
  const showTerrain = visitContext?.activeTab === 'terrain' && !!terrain;

  // The right column's toggle only exists on the Patient chart tab when the host folds it; no fold here, the column is always open.
  /*
   * THE LAYOUT IS THE DS `PreEncounterScreen` (3A lane DS-B, graduated from this file):
   *   · the published Flutter column law (PROD-1549, Ahmed 2026-09-05, 1920 / zoom 100 % / DPR 1):
   *     rail + main column together = `max(969, 0.7 × width)` while the note panel is inline, the
   *     pathway/note panel is the remainder, 10px between rail and main, no outer padding. Below
   *     Flutter's 1384 breakpoint the group fills the row — the template tracks the width itself.
   *   · CHART TAB (PROD-1986): `chart` renders IN PLACE of the body, the patient-history band on
   *     top clamped to Flutter's `patientInfoMinHeight` (180) and clipped, the right rail still
   *     mounted beside it at full height (Andrew, 2026-08-19 / 2026-08-20) — Flutter's
   *     `chartViewMainPanel` + `sidePanel()` sibling (encounter_screen.dart:1353, :1657).
   *   · CHROME: the tab strip, then the virtual-visit advisory — Flutter mounts it exactly there,
   *     under the tabs and above the columns (encounter_screen.dart:955-974), gated on a VIRTUAL
   *     location. Without `visitContext` the pane is the root, exactly as before.
   *
   * RIGHT COLUMN — the Care-pathway rail inside the DS `EncounterNoteDock` (bare: the rail is
   * already its own column). Below Flutter's 1384px breakpoint it collapses to the bottom-right
   * Notes card, the same chrome the started encounter uses — Flutter builds that card once for
   * both (`generatedNotesAsPopUp()`, gated on the encounter tab and not on view state). This
   * SUPERSEDES the PROD-1588 wave-S2 "no right drawer pre-encounter" ruling for this screen: that
   * verdict was about the empty Note/Orders DRAWER, not a pathway timeline.
   */
  const pathway = (
    <EncounterNoteDock inline="bare">
      <CarePathwayRailContainer {...railProps} />
    </EncounterNoteDock>
  );
  // The right column: the Intelligence rail when `?x=previsit-intel` and the page passed one; else Care pathway / Note.
  // PROD-3388: the rail starts open; its header X closes it, and then the column is empty and the orb button bottom right
  // (IntelligenceFab, the same as the started encounter's) reopens it.
  // `?x=intel-always-open` (the zoning): the rail is the column on EVERY tab, and whether it is open is the zoning's (the
  // clinician's choice, held for the page session, and the 480 floor).
  const intelRail = zoning != null || (showIntel && !!intelligence);
  // Under the 480 floor (the zoning's overlay), the rail is drawn over the centre, not docked.
  const intelDocked = intelOpen && !zoning?.panelOverlay;
  // With the zoning the rail is mounted ONCE (`intel-zone`, after the screen, below) and only its placement changes; the
  // screen's row holds a stateless spacer that reserves Antonin's 400 (R-AI-01) while it is docked. w-100 = 400 on the 4 px
  // spacing scale (the DS has no 400 panel token; `--zone-panel` is 360), and `shrink-0` keeps it (dev 4c57ba56 at 1440: the
  // rail ran 60 px past the body). Moving the spacer between tabs remounts nothing that holds state (Codex, fourth pass).
  const spacer = <div data-slot="intel-spacer" aria-hidden className="w-100 min-w-100 max-w-100 shrink-0" />;
  const rightColumn = zoning
    ? intelDocked
      ? spacer
      : undefined
    : intelRail
      ? intelOpen
        ? intelligence
        : undefined
      : hidePathway
        ? undefined
        : pathway;
  const onEncounterTab = !showDocuments && !showTimeline && !showTerrain && !showChart;
  // The Documents tab with the zoning: ONE tree position for the documents whatever the rail does (Codex, fourth pass).
  // Terrain carries the Documents scroller's slot since PR 967, so the same bridge rule gives it the whole row: the rail rides
  // inside its row too, or it is pushed off the screen.
  const docsWithRail = !!zoning && (showDocuments || showTerrain);

  // The page's one primary action lives in the step bar, one layer under the page header (Antonin, 15 Oct: "the continue
  // button should be one container under, with a stepper like in the prototype. No button near tabs in header"): Start /
  // Continue, Reschedule and the reason Start is off, the same handlers and the same disabled state as before. The
  // Chart tab replaces the workflow, so the step bar shows on the Encounter tab only (the prototype's stepper lives in
  // its encounter view, never on the chart). `actionsInHeader` keeps the workflow block from drawing them a second time.
  const actionsInHeader = !!visitContext && !!patient;
  const barRow = actionsInHeader && !showChart && !showDocuments && !showTimeline && !showTerrain;
  const startActions =
    barRow ? (
      <>
        {startDisabledReason ? (
          <p data-slot="visit-start-reason" title={startDisabledReason} className="m-0 truncate">
            {startDisabledReason}
          </p>
        ) : null}
        {onReschedule ? (
          <Button variant="secondary" size="sm" onClick={onReschedule}>
            Reschedule
          </Button>
        ) : null}
        <Button size="sm" onClick={onStart} disabled={Boolean(startDisabledReason)}>
          {preStartLabel(startAction ?? 'start')}
        </Button>
      </>
    ) : undefined;

  const videoNotice =
    visitContext && reason.locationType === 'virtual' ? (
      <VideoJoinNotice
        inCall={!!videoInCall}
        onConnect={onVideoConnect}
        onLeave={onVideoLeave}
        reason={videoJoinReason}
      />
    ) : undefined;

  const workflowEl = (
    <EncounterWorkflow
      actionsInHeader={actionsInHeader}
      reason={reason}
      startAction={startAction}
      startDisabledReason={startDisabledReason}
      onStart={onStart}
      onReschedule={onReschedule}
      chiefComplaint={chiefComplaint}
      onChiefComplaintChange={onChiefComplaintChange}
      ccSuggestions={ccSuggestions}
      ccSelected={ccSelected}
      onCcSelectionChange={onCcSelectionChange}
      onCcCreate={onCcCreate}
      ccSaving={ccSaving}
      chiefComplaintDeferred={chiefComplaintDeferred}
      chiefComplaintLocked={chiefComplaintLocked}
      vitals={vitals}
      patientType={patientType}
      onVitalsReviewed={onVitalsReviewed}
      canReviewVitals={canReviewVitals}
      onVitalsHistorical={onVitalsHistorical}
      onVitalsEdit={onVitalsEdit}
      onVisualAcuity={onVisualAcuity}
      vitalsReviewed={vitalsReviewed}
      onVitalsSubmit={onVitalsSubmit}
      vitalsExisting={vitalsExisting}
      vitalsSaving={vitalsSaving}
      vitalsNotice={vitalsNotice}
      deviceData={deviceData}
      ambientCard={ambientCard}
      nursePrevisit={nursePrevisit}
      previsitDiagnostics={previsitDiagnostics}
      patientGender={patientGender}
      patientAge={patientAge}
      visualAcuity={visualAcuity}
      onAcuityCancel={onAcuityCancel}
      visualAcuitySummary={visualAcuitySummary}
      growthChart={growthChart}
      submittedForm={submittedForm}
      submittedFormError={submittedFormError}
      // First name only — the "… has not answered the questionnaire yet." copy (Flutter uses
      // patient.firstName). The rail carries the display name, so take its leading token.
      patientFirstName={patient.name?.trim().split(/\s+/)[0] || undefined}
      activeMedications={activeMedications}
      vaccinationRequests={vaccinationRequests}
      onAdministerVaccination={onAdministerVaccination}
      previousNotes={previousNotes}
      onViewPreviousNote={onViewPreviousNote}
      patientId={patientId}
      encounterId={encounterId}
      encounterRole={encounterRole}
      medicationAdminRequests={medicationAdminRequests}
      onGiveMedication={onGiveMedication}
      nurseNotes={nurseNotes}
      nurseNotesNode={nurseNotesNode}
      loading={loading}
    />
  );

  const screen = (
    <PreEncounterScreen
      topBar={
        visitContext ? (
          <>
            <PreEncounterTabStrip
              patient={patient}
              demographics={demographics}
              activeTab={visitContext.activeTab}
              onTabChange={visitContext.onTabChange}
              documentsAvailable={!!documents}
              timelineAvailable={!!timeline}
              timelineLabel={timelineLabel}
              terrainAvailable={!!terrain}
              // No Back chevron in the pre-encounter header: the top bar's breadcrumb (Patients › name) is the way back.
              {...(oneHeader
                ? {
                    oneLook: true,
                    status: <RailStatusControl appointmentStatus={appointmentStatus} appointmentId={appointmentId} />,
                    // The one header is where the rail (rail-v3) drops its card with `?x=rail-fixes`: its importance and language come here.
                    importance,
                    cardMoved: true,
                  }
                : {})}
              onSearch={visitContext.onSearch}
              onOpenMalaffi={visitContext.onOpenMalaffi}
              // Staff chat (SR-14): message a colleague about this patient. Only with `?x=staff-chat` and
              // `?x=staff-chat-patient` (patient threads wait for Samer's call). Nothing when off.
              headerAction={isStaffChatPatientOn() ? <MessageColleagueAction patientId={patientId} /> : undefined}
              // The left panel's toggle sits on the header's bottom line, as in the started encounter (decided 28 Oct; it
              // was on the step bar's seam from 16 Oct). The Chart tab has nothing to fold.
              leftToggle={showChart ? undefined : <LeftPanelToggle folded={leftFolded} onToggle={toggleLeft} />}
            />
            {/* The video-visit notice sits under the page header (16 Oct, Antonin). */}
            {barRow ? videoNotice : null}
          </>
        ) : undefined
      }
      notice={barRow ? undefined : videoNotice}
      summary={leftRail}
      main={
        barRow ? (
          /* The step bar is the top of the CENTRAL column only (Antonin, 19 Oct: "lower, in the main frame, not between header and
             the main + left and right rails"): the side panels start right under the page header, and the bar stays put while the
             column's content scrolls under it (bridge block "encounter step bar: central column"). */
          <div data-slot="pre-encounter-central">
            <EncounterStepBar
              steps={PRE_VISIT_STEPS}
              activeId={PRE_VISIT_STEP.id}
              actions={startActions}
            />
            {workflowEl}
          </div>
        ) : showDocuments ? (
          docsWithRail ? (
            /* The Documents tab with the zoning: the bridge gives this tab's main group the whole row (width 100%, no shrink,
               showcase-bridge.css, frozen), so the rail rides INSIDE the group, beside the documents, which shrink to the rest. */
            <div data-slot="intel-docs-row" className="flex min-h-0 min-w-0 flex-1">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">{documents}</div>
              {rightColumn ?? null}
            </div>
          ) : (
            documents
          )
        ) : showTimeline ? (
          timeline
        ) : showTerrain ? (
          docsWithRail ? (
            <div data-slot="intel-docs-row" className="flex min-h-0 min-w-0 flex-1">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">{terrain}</div>
              {rightColumn ?? null}
            </div>
          ) : (
            terrain
          )
        ) : (
          workflowEl
        )
      }
      // With the zoning, Scrips Intelligence stays on every tab (Samer, 4 Nov); without it the other tabs have no right column.
      pathway={docsWithRail ? undefined : zoning || onEncounterTab ? rightColumn : undefined}
      // PROD-1986: gated on the slot so hosts that pass no chart (Storybook / no-patient paths)
      // are untouched.
      chart={showChart ? chart : undefined}
      chartBand={chartBand}
    />
  );
  if (!intelRail || (!intelCloseOn && !zoning)) return screen;
  return (
    <>
      <IntelligenceCloseContext.Provider value={closeIntel}>
        {zoning ? (
          /* `?x=intel-always-open`: the rail's ONE mount, at the screen's right edge under the page header and any notice under it (its top is
             measured: `--intel-host-top`, set below). Docked it lies over the spacer the row reserves; under the 480 floor it is the overlay over
             the centre (Antonin fc326b6d, "reopens as an overlay"; a dialog); closed it is hidden, still mounted, so the Ask
             draft and the brief survive a resize or a Close (Codex, fourth pass). Layout only; the rail paints itself. */
          <div data-slot="intel-zone-row" className="relative flex h-full min-h-0 flex-col">
            {screen}
            <div
              ref={overlayRef}
              data-slot={zoning.panelOverlay ? 'intel-overlay' : 'intel-zone'}
              role={zoning.panelOverlay ? 'dialog' : undefined}
              aria-label={zoning.panelOverlay ? 'Scrips Intelligence panel' : undefined}
              hidden={!intelOpen}
              className="absolute top-[var(--intel-host-top)] right-0 bottom-0 z-40 flex w-100 min-w-100 max-w-100 flex-col overflow-hidden"
            >
              {intelligence}
            </div>
          </div>
        ) : (
          screen
        )}
      </IntelligenceCloseContext.Provider>
      {(onEncounterTab || zoning) && !intelOpen ? (
        <IntelligenceFab ref={intelFabRef} onOpen={openIntel}>
          {intelligenceStrip}
        </IntelligenceFab>
      ) : null}
    </>
  );
}
